import 'katex/dist/katex.min.css';
import Markdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { BuildClass } from '../../../../universal';
import { _, React } from '../../../lib';
const ALERT_CONFIG = {
    note: {
        label: 'Note',
        className: 'bg-[oklch(var(--chat-l-text)_0_0_/_0.035)] border-[oklch(var(--chat-l-text)_0_0_/_0.28)]',
        titleClassName: 'text-[oklch(var(--chat-l-text-normal)_0_0)]',
    },
    tip: {
        label: 'Tip',
        className: 'bg-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_145_/_0.08)] border-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_145_/_0.55)]',
        titleClassName: 'text-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_145)]',
    },
    important: {
        label: 'Important',
        className: 'bg-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_var(--chat-hue)_/_0.08)] border-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_var(--chat-hue)_/_0.6)]',
        titleClassName: 'text-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_var(--chat-hue))]',
    },
    warning: {
        label: 'Warning',
        className: 'bg-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_85_/_0.1)] border-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_85_/_0.65)]',
        titleClassName: 'text-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_85)]',
    },
    caution: {
        label: 'Caution',
        className: 'bg-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_25_/_0.08)] border-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_25_/_0.6)]',
        titleClassName: 'text-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_25)]',
    },
};
const parseAlertType = (value) => {
    switch (value.toUpperCase()) {
        case 'NOTE':
            return 'note';
        case 'TIP':
            return 'tip';
        case 'IMPORTANT':
            return 'important';
        case 'WARNING':
            return 'warning';
        case 'CAUTION':
            return 'caution';
        default:
            return null;
    }
};
const parseAlertMarker = (value) => {
    const match = /^\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*/i.exec(value);
    if (!match) {
        return null;
    }
    const type = match[1] == null ? null : parseAlertType(match[1]);
    if (type == null) {
        return null;
    }
    return {
        type,
        text: value.slice(match[0].length),
    };
};
const stripAlertMarker = (children) => {
    const childArray = React.Children.toArray(children);
    const firstChildIndex = childArray.findIndex(child => !(_.isString(child) && child.trim().length === 0));
    const firstChild = childArray[firstChildIndex];
    if (!React.isValidElement(firstChild)) {
        return null;
    }
    if (firstChild.type !== 'p') {
        return null;
    }
    const paragraphChildren = React.Children.toArray(firstChild.props.children);
    const firstParagraphChildIndex = paragraphChildren.findIndex(child => !(_.isString(child) && child.trim().length === 0));
    const firstParagraphChild = paragraphChildren[firstParagraphChildIndex];
    if (!_.isString(firstParagraphChild)) {
        return null;
    }
    const parsed = parseAlertMarker(firstParagraphChild);
    if (!parsed) {
        return null;
    }
    const updatedParagraphChildren = paragraphChildren
        .map((child, index) => (index === firstParagraphChildIndex ? parsed.text : child))
        .filter(child => !(_.isString(child) && child.length === 0));
    const updatedChildren = childArray.map((child, index) => index === firstChildIndex
        ? updatedParagraphChildren.length === 0
            ? null
            : React.cloneElement(firstChild, undefined, updatedParagraphChildren)
        : child);
    return { type: parsed.type, children: updatedChildren };
};
export const MarkdownRenderer = (props) => (React.createElement("div", { className: BuildClass([
        props.largeText ? 'text-base leading-snug' : 'text-sm leading-relaxed',
        'text-[oklch(var(--chat-l-text)_0_0)]',
        'text-left',
        'prose prose-sm max-w-none',
        '[&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-2',
        '[&_h1]:text-[oklch(var(--chat-l-text)_0_0)]',
        '[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-2',
        '[&_h2]:text-[oklch(var(--chat-l-text)_0_0)]',
        '[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1',
        '[&_h3]:text-[oklch(var(--chat-l-text)_0_0)]',
        '[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
        '[&_ul]:my-2 [&_ul]:pl-5 [&_ul]:list-disc',
        '[&_ol]:my-2 [&_ol]:pl-5 [&_ol]:list-decimal',
        '[&_li]:my-0.5',
        '[&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded',
        '[&_code]:bg-[oklch(var(--chat-l-code)_0_0)]',
        '[&_code]:text-[oklch(var(--chat-l-text-normal)_0_0)]',
        '[&_code]:text-[0.85em] [&_code]:font-mono',
        '[&_pre]:my-2 [&_pre]:p-3 [&_pre]:rounded-md [&_pre]:overflow-x-auto',
        '[&_pre]:bg-[oklch(var(--chat-l-code)_0_0)]',
        '[&_pre]:text-[oklch(var(--chat-l-text-content)_0_0)]',
        '[&_pre_code]:p-0 [&_pre_code]:bg-transparent',
        '[&_a]:text-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_var(--chat-hue))]',
        '[&_a]:underline [&_a]:underline-offset-2',
        '[&_a:hover]:text-[oklch(calc(var(--chat-l-accent)-0.1)_var(--chat-c-accent)_var(--chat-hue))]',
        '[&_blockquote]:my-2',
        '[&_blockquote:not([data-markdown-alert])]:pl-4',
        '[&_blockquote:not([data-markdown-alert])]:border-l-2 [&_blockquote:not([data-markdown-alert])]:border-[oklch(var(--chat-l-text)_0_0_/_0.3)]',
        '[&_blockquote:not([data-markdown-alert])]:text-[oklch(var(--chat-l-text-normal)_0_0)]',
        '[&_hr]:my-4 [&_hr]:border-[oklch(var(--chat-l-text)_0_0_/_0.2)]',
        '[&_table]:my-2 [&_table]:w-full [&_table]:text-left',
        '[&_th]:p-2 [&_th]:border-b [&_th]:border-[oklch(var(--chat-l-text)_0_0_/_0.2)] [&_th]:font-semibold',
        '[&_td]:p-2 [&_td]:border-b [&_td]:border-[oklch(var(--chat-l-text)_0_0_/_0.1)]',
        '[&_.katex-display]:my-3 [&_.katex-display]:overflow-x-auto [&_.katex-display]:overflow-y-hidden',
        '[&_.katex-display]:py-1 [&_.katex-display]:text-left',
        '[&_.katex]:text-[1.05em]',
        '[&_strong]:font-semibold',
        '[&_em]:italic [&_em]:text-inherit',
        props.className,
    ]) },
    React.createElement(Markdown, { remarkPlugins: [remarkGfm, [remarkMath, { singleDollarTextMath: false }]], rehypePlugins: [rehypeKatex], components: {
            a: innerProps => (React.createElement("a", { href: innerProps.href, target: "_blank", rel: "noopener noreferrer" }, innerProps.children)),
            code: innerProps => (React.createElement("code", { className: innerProps.className, ..._.omit(innerProps, [
                    'className',
                    'children',
                    'node',
                    'index',
                    'siblingCount',
                    'style',
                ]) }, innerProps.children)),
            blockquote: innerProps => {
                const alert = stripAlertMarker(innerProps.children);
                if (!alert) {
                    return React.createElement("blockquote", null, innerProps.children);
                }
                const config = ALERT_CONFIG[alert.type];
                return (React.createElement("blockquote", { "data-markdown-alert": alert.type, className: BuildClass([
                        'rounded px-3 py-2',
                        'border-l-4',
                        config.className,
                    ]) },
                    React.createElement("div", { className: BuildClass([
                            'mb-1 text-[11px] font-semibold uppercase',
                            config.titleClassName,
                        ]) }, config.label),
                    React.createElement("div", null, alert.children)));
            },
        } }, props.content)));
export const InlineMarkdown = (props) => (React.createElement("span", { className: BuildClass([
        '[&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded',
        '[&_code]:bg-[oklch(var(--chat-l-text)_0_0_/_0.1)]',
        '[&_code]:text-[oklch(var(--chat-l-text-normal)_0_0)]',
        '[&_code]:text-[0.9em] [&_code]:font-mono',
        '[&_strong]:font-semibold',
        '[&_em]:italic',
        '[&_a]:underline [&_a]:text-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_var(--chat-hue))]',
        props.className,
    ]) },
    React.createElement(Markdown, { allowedElements: ['strong', 'em', 'code', 'a'], unwrapDisallowed: true, components: {
            a: innerProps => (React.createElement("a", { href: innerProps.href, target: "_blank", rel: "noopener noreferrer" }, innerProps.children)),
        } }, props.content)));
//# sourceMappingURL=markdown-renderer.js.map